/**
 * Photon client — browser-direct forward + reverse geocoding.
 *
 * Phase 3B of the Memory-Map work uses Komoot's hosted Photon API
 * (https://photon.komoot.io/) for address autocomplete. Photon is:
 *   - Free, no API key
 *   - CORS-allowed for direct browser calls (Komoot operates it
 *     specifically for autocomplete use cases)
 *   - ~50ms latency
 *   - Built on OSM data with autocomplete-optimized ranking
 *
 * This file flattens Photon's nested `properties` into the simple
 * shape the FE saves (lat, lng, plus structured address fields:
 * street, city, state, postcode, country, name).
 *
 * Rate-limit politeness — caller is expected to debounce input by
 * ~300ms + abort previous request on each new keystroke. This file
 * itself does not throttle; the consumer (LocationEditSheet) does.
 */

/** Photon's raw response shape — see https://photon.komoot.io/ docs.
 *  We type the bits we read; everything else is unknown. */
interface PhotonProperties {
  housenumber?: string;
  street?: string;
  name?: string;          // POI / place name (e.g. "Calgary Tower")
  city?: string;
  locality?: string;      // backup for rural areas
  district?: string;      // city subdivision
  town?: string;          // small towns may use this instead of city
  village?: string;       // village name; rural fallback
  state?: string;
  postcode?: string;
  country?: string;       // full country name (e.g. "Canada")
  countrycode?: string;   // ISO-3166 alpha-2 (e.g. "CA")
  osm_id?: number;
  osm_type?: string;
  type?: string;          // 'house', 'street', 'locality', etc.
}

interface PhotonFeature {
  type: 'Feature';
  geometry: { type: 'Point'; coordinates: [number, number] };
  properties: PhotonProperties;
}

interface PhotonResponse {
  features: PhotonFeature[];
}

/**
 * Simplified address candidate consumed by LocationEditSheet.
 * Flat shape — no nested objects.
 */
export interface AddressCandidate {
  /** Human-readable label for the dropdown / picked-state pill */
  display_name: string;
  /** Decimal lat. Photon returns [lng, lat] order — we swap. */
  lat: number;
  lng: number;
  /** Street + house number combined (e.g. "100 Main Street"). null if Photon returned neither. */
  street: string | null;
  city: string | null;
  state: string | null;
  postcode: string | null;
  /** ISO-3166 alpha-2 (uppercased) if Photon returned countrycode; else null. */
  country: string | null;
  /** POI / place name when applicable (Calgary Tower) — used for nicer display labels. */
  name: string | null;
}

const FORWARD_URL = 'https://photon.komoot.io/api';
const REVERSE_URL = 'https://photon.komoot.io/reverse';
const DEFAULT_LANG = 'en';

/** Build the display label for the dropdown row. Prefer POI name +
 *  city when available; fall back to street + city; fall back to
 *  whatever non-empty fields exist. */
function buildDisplayName(p: PhotonProperties): string {
  const street = [p.housenumber, p.street].filter(Boolean).join(' ').trim();
  const place = p.name && !p.street ? p.name : null; // POI takes precedence only if no street
  const city = p.city || p.town || p.village || p.locality || p.district;

  const parts: string[] = [];
  if (place) parts.push(place);
  if (street) parts.push(street);
  if (city) parts.push(city);
  if (p.state) parts.push(p.state);
  if (p.country) parts.push(p.country);

  return parts.filter(Boolean).join(', ') || 'Unknown location';
}

/** Map a Photon feature to a flat AddressCandidate. */
function toCandidate(feature: PhotonFeature): AddressCandidate {
  const p = feature.properties || {};
  const coords = feature.geometry?.coordinates;
  // Photon returns [longitude, latitude]
  const lng = Array.isArray(coords) ? coords[0] : NaN;
  const lat = Array.isArray(coords) ? coords[1] : NaN;
  const street = [p.housenumber, p.street].filter(Boolean).join(' ').trim() || null;
  const city = p.city || p.town || p.village || p.locality || p.district || null;
  const country = p.countrycode ? p.countrycode.toUpperCase() : null;
  return {
    display_name: buildDisplayName(p),
    lat,
    lng,
    street,
    city,
    state: p.state || null,
    postcode: p.postcode || null,
    country,
    name: p.name || null,
  };
}

/**
 * Forward search — text query → ranked candidates.
 *
 * @param query  user's typed input (3+ chars recommended for relevance)
 * @param signal AbortSignal from the caller — used to cancel in-flight
 *               requests when a new keystroke fires
 * @param limit  max suggestions (default 5)
 * @returns      array of AddressCandidate (up to `limit`); empty array
 *               on no results, network error, or abort
 */
export async function searchAddress(
  query: string,
  signal?: AbortSignal,
  limit = 5,
): Promise<AddressCandidate[]> {
  const trimmed = (query || '').trim();
  if (trimmed.length < 3) return [];
  const url = new URL(FORWARD_URL);
  url.searchParams.set('q', trimmed);
  url.searchParams.set('limit', String(limit));
  url.searchParams.set('lang', DEFAULT_LANG);
  try {
    const res = await fetch(url.toString(), { signal });
    if (!res.ok) return [];
    const data: PhotonResponse = await res.json();
    return (data.features || []).map(toCandidate)
      .filter((c) => Number.isFinite(c.lat) && Number.isFinite(c.lng));
  } catch (err) {
    // Aborted requests + network errors fail soft — caller renders
    // "no suggestions" state. Real bugs would still surface via the
    // consumer's catch.
    if ((err as Error)?.name === 'AbortError') return [];
    return [];
  }
}

/**
 * Reverse — lat/lng → single best candidate.
 *
 * Used when the user taps the inline GPS button: we capture the
 * phone's coords (via captureScanLocation), then call Photon reverse
 * to get a human-readable address text + structured fields.
 *
 * @returns null when Photon returns nothing usable; caller falls back
 *          to showing raw "lat, lng" text in the input.
 */
export async function reverseGeocode(
  lat: number,
  lng: number,
  signal?: AbortSignal,
): Promise<AddressCandidate | null> {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  const url = new URL(REVERSE_URL);
  url.searchParams.set('lat', String(lat));
  url.searchParams.set('lon', String(lng));
  url.searchParams.set('lang', DEFAULT_LANG);
  try {
    const res = await fetch(url.toString(), { signal });
    if (!res.ok) return null;
    const data: PhotonResponse = await res.json();
    const first = data.features?.[0];
    return first ? toCandidate(first) : null;
  } catch (err) {
    if ((err as Error)?.name === 'AbortError') return null;
    return null;
  }
}
